/**
 * Eigenbau: Profilmenü auf der Tafel — vom Tremor-Block blieb nichts übrig.
 *
 * Angefangen als `template-insights`/DropdownUserProfile, inzwischen aber
 * vollständig ersetzt: eigene Flächen und Rollen, eigene Anordnung, eigener
 * Inhalt. Die Bausteine `DropdownMenu*` kommen weiter aus dem Bestand, die
 * Vorlage selbst trägt hier nichts mehr bei — deshalb `Eigenbau` und nicht
 * `Herkunft`.
 *
 * Profilmenü des Studio-Entwurfs.
 *
 * Das Menü öffnet aus der dauerhaft dunklen Schiene und bleibt deshalb auf
 * derselben Tafel. Eigene Tafel-Rollen sind hier wichtig: Die gewöhnlichen
 * Papier-/Tintenfarben wechseln mit dem Theme und wären im hellen Modus auf
 * der Schiene ein Fremdkörper.
 *
 * Der Inhalt ist bewusst klein: echte Kontodaten, Einstellungen,
 * Darstellung und Abmelden. Die funktionslosen Tremor-Beispieleinträge sind
 * entfernt. Navigation und Abmelden sind Handlungen des `menuitem` selbst,
 * damit keine interaktiven Links ineinander verschachtelt werden.
 */
"use client"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/DropdownMenu"
import { melde_ab } from "@/lib/auth"
import { useIch, usePerson } from "@/lib/haken"
import { siteConfig } from "@/siteConfig"
import { useNavigate } from "@tanstack/react-router"
import { useTheme } from "next-themes"
import * as React from "react"

export type DropdownUserProfileProps = {
  children: React.ReactNode
  align?: "center" | "start" | "end"
}

/*
 * Die Breite kommt vom Auslöser, nicht aus einer Zahl.
 *
 * Vorher stand hier `w-[220px]`. Der Auslöser ist 192px breit (236px Schiene
 * minus zweimal 22px Rand), und bei `align="start"` beginnt das Menü auf
 * seiner linken Kante — es endete damit bei 242px und stand **sechs Pixel
 * über der Schiene**, auf dem Papier. Radix weicht nur dem Fensterrand aus,
 * nicht einer Leiste, deshalb hat auch `collisionPadding` das nicht bemerkt.
 *
 * `--radix-dropdown-menu-trigger-width` ist die gemessene Breite des
 * Auslösers. Damit steht das Menü auf beiden Seiten bündig mit der Zeile, zu
 * der es gehört — und kann per Bauart nicht überstehen.
 */
const TAFEL_FLAECHE =
  "z-[60] w-[var(--radix-dropdown-menu-trigger-width)] !min-w-0 max-w-[calc(100vw-24px)] overflow-x-hidden overflow-y-auto rounded-none border-tafel-linie bg-tafel p-0 text-tafel-tinte shadow-none motion-reduce:animate-none dark:border-tafel-linie dark:bg-tafel dark:text-tafel-tinte"

const TAFEL_EINTRAG =
  "min-h-9 rounded-none px-3 py-2 text-[13px] font-light text-tafel-tinte transition-colors duration-150 ease-aus hover:bg-tafel-wisch hover:text-tafel-tinte focus-visible:bg-tafel-wisch focus-visible:text-tafel-akzent data-[highlighted]:bg-tafel-wisch data-[highlighted]:text-tafel-akzent data-[state=open]:bg-tafel-wisch data-[state=open]:text-tafel-akzent dark:text-tafel-tinte dark:hover:bg-tafel-wisch dark:hover:text-tafel-tinte dark:focus-visible:bg-tafel-wisch dark:focus-visible:text-tafel-akzent data-[state=open]:dark:bg-tafel-wisch data-[state=open]:dark:text-tafel-akzent motion-reduce:transition-none"

const TAFEL_RADIO =
  `${TAFEL_EINTRAG} pl-9 pr-3 data-[state=checked]:font-medium data-[state=checked]:text-tafel-akzent dark:data-[state=checked]:text-tafel-akzent`

const TAFEL_TRENNER =
  "mx-0 my-0 border-tafel-linie dark:border-tafel-linie"

export function DropdownUserProfile({
  children,
  align = "start",
}: DropdownUserProfileProps) {
  const [mounted, setMounted] = React.useState(false)
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const ich = useIch()
  const person = usePerson(ich.data?.personId)
  /*
   * Hat ein Zeiger das Menü geschlossen?
   *
   * Radix gibt den Fokus beim Schließen an den Auslöser zurück. Das ist für
   * die Tastatur richtig — wer mit Esc schließt, steht danach wieder auf dem
   * Knopf. Nach einem Klick ins Leere ist es aber falsch sichtbar: Der Browser
   * wertet den zurückgereichten Fokus als `:focus-visible` und zeichnet einen
   * 2px-Umriss um den Namen. Genau das sah aus, als wäre der Benutzername
   * markiert.
   *
   * Deshalb merken wir uns die Art des Schließens und unterdrücken den
   * Rücksprung nur beim Zeiger. Die Tastatur behält ihn.
   */
  const zeigerSchloss = React.useRef(false)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side="top"
        sideOffset={10}
        collisionPadding={12}
        className={TAFEL_FLAECHE}
        onPointerDownOutside={() => {
          zeigerSchloss.current = true
        }}
        onCloseAutoFocus={(ereignis) => {
          if (!zeigerSchloss.current) return
          zeigerSchloss.current = false
          ereignis.preventDefault()
        }}
      >
        <DropdownMenuLabel className="px-3 py-3 text-tafel-tinte dark:text-tafel-tinte">
          <span className="block truncate text-[13px] font-normal text-tafel-tinte">
            {person.data?.name ?? "Wird geladen …"}
          </span>
          <span className="mt-0.5 block truncate font-mono text-[10.5px] font-normal text-tafel-tinte3">
            {person.data?.email ?? "Konto wird geladen …"}
          </span>
        </DropdownMenuLabel>

        <DropdownMenuGroup>
          <DropdownMenuItem
            className={TAFEL_EINTRAG}
            onSelect={() => {
              void navigate({
                to: siteConfig.baseLinks.einstellungen.persoenlich,
              })
            }}
          >
            Einstellungen
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator className={TAFEL_TRENNER} />
        <DropdownMenuLabel className="px-3 pb-1.5 pt-3 font-mono text-[10.5px] font-normal tracking-normal text-tafel-rubrik dark:text-tafel-rubrik">
          Darstellung
        </DropdownMenuLabel>
        <DropdownMenuGroup>
          <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
            <DropdownMenuRadioItem value="light" className={TAFEL_RADIO}>
              Hell
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark" className={TAFEL_RADIO}>
              Dunkel
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system" className={TAFEL_RADIO}>
              System
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>

        <DropdownMenuSeparator className={TAFEL_TRENNER} />
        <DropdownMenuGroup>
          <DropdownMenuItem className={TAFEL_EINTRAG} onSelect={melde_ab}>
            Abmelden
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
